import { ARCANA_SLUG, MATRIX_ARCANA } from '@/api/consts'
import type { MatrixArcanaResponse } from '@/api/schema/matrix-arcana'
import { CalcBanner } from '@/app/components/CalcBanner/CalcBanner'
import { Faq } from '@/app/components/Faq/Faq'
import { Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { MenuFooter } from '@/app/components/MenuFooter/MenuFooter'
import { Motion } from '@/app/components/Motion/Motion'
import { ScrollToTop } from '@/app/components/ScrollToTop/ScrollToTop'
import { useBack } from '@/app/hooks/useBack'
import { ArcanaFooter } from '@/app/pages/MatrixArcanaPage/Footer/Footer'
import { ArcanaHead } from '@/app/pages/MatrixArcanaPage/Head/Head'
import { PlusMinus } from '@/app/pages/MatrixArcanaPage/PlusMinus/PlusMinus'
import { api } from '@/app/utils/api'
import { useAppData } from '@/app/utils/app-data'
import type { FunctionalComponent } from 'preact'
import { useLayoutEffect, useState } from 'preact/hooks'
import { useParams } from 'wouter-preact'
import styles from './index.module.scss'

type MatrixArcanaPageType = 'Base' | 'Money'

type MatrixArcanaPageConfig = {
  getCachedData: (appData: ReturnType<typeof useAppData>, id: number) => MatrixArcanaResponse | null
  apiPath: (id: number) => string
  pagePath: string
  relatedPagePath: string
  titleSuffix: string
  relatedTitleSuffix: string
  relatedListText: string
  imageAltSuffix: string
  calcBannerDesc: string
  cardHint: string
}

const CONFIG: Record<MatrixArcanaPageType, MatrixArcanaPageConfig> = {
  Base: {
    getCachedData: (appData, id) => appData.getMatrixArcana(id),
    apiPath: id => `/guide/matrix-arcana/${id}`,
    pagePath: '~/guide/matrix-arcana',
    relatedPagePath: '~/guide/matrix-money',
    titleSuffix: 'в Матрице судьбы',
    relatedTitleSuffix: 'в денежном коде',
    relatedListText: 'Все арканы денежного кода',
    imageAltSuffix: 'в Матрице судьбы',
    calcBannerDesc:
      'Аркан раскрывается точнее, когда видны его позиция и связи. Открой полную Матрицу, чтобы увидеть свою картину целиком.',
    cardHint: 'Матрица судьбы',
  },
  Money: {
    getCachedData: (appData, id) => appData.getMoneyArcana(id),
    apiPath: id => `/matrix-money/${id}`,
    pagePath: '~/guide/matrix-money',
    relatedPagePath: '~/guide/matrix-arcana',
    titleSuffix: 'в денежном коде',
    relatedTitleSuffix: 'в Матрице судьбы',
    relatedListText: 'Все арканы Матрицы судьбы',
    imageAltSuffix: 'в денежном коде Матрицы судьбы',
    calcBannerDesc: 'Денежный код — только одна точка. Открой полную Матрицу, чтобы увидеть все арканы и связи между ними.',
    cardHint: 'Денежный код',
  },
}

const ARCANA_COUNT = Object.keys(MATRIX_ARCANA).length

function getNextArcanaLinks(currentArcanaId: number, pagePath: string) {
  const arcanas = [1, 2, 3, 4].map((offset) => {
    const id = ((currentArcanaId - 1 + offset) % ARCANA_COUNT) + 1

    return {
      id,
      name: MATRIX_ARCANA[id].name,
      href: `${pagePath}/${id}-${ARCANA_SLUG[id]}`,
    }
  })

  return [
    arcanas[0],
    arcanas[2],
    arcanas[1],
    arcanas[3],
  ]
}

export const MatrixArcanaPage: FunctionalComponent<{
  type: MatrixArcanaPageType
}> = props => {
  const back = useBack('~/tools')
  const appData = useAppData()
  const params = useParams<{ id: string }>()
  const arcanaId = Number(params.id.split('-')[0])
  const config = CONFIG[props.type]
  const initialData = config.getCachedData(appData, arcanaId)

  const [data, setData] = useState<MatrixArcanaResponse | null>(initialData)

  useLayoutEffect(() => {
    const cachedData = config.getCachedData(appData, arcanaId)

    if (cachedData) {
      setData(cachedData)
      return
    }

    setData(null)

    api.get<MatrixArcanaResponse>(config.apiPath(arcanaId))
      .then(resp => {
        setData(resp.data)
      })
  }, [arcanaId, appData, config])

  return (
    <Layout key={`${props.type}-${arcanaId}`}>
      <Layout.Content
        onBack={back}
      >
        {!data
          ? (
            <Loader.Container>
              <Loader type='matrix' />
            </Loader.Container>
          )
          : (
            <Motion class={styles.root}>
              <ScrollToTop />

              <Layout.Header type='secondary'>
                Матрица судьбы
              </Layout.Header>

              <Row gap={30} gapMobile={20}>
                <Row gap={15}>
                  <ArcanaHead
                    title={`${data.arcana.id} Аркан ${config.titleSuffix} — ${data.arcana.name}`}
                    description={data.data.description}
                    arcanaId={arcanaId}
                    imageSrc={`/tarot/${data.arcana.tarot}.jpg`}
                    imageAlt={`Карта ${data.arcana.id} аркана «${data.arcana.name}» ${config.imageAltSuffix}`}
                    cardHint={config.cardHint}
                  />

                  <PlusMinus
                    plusTitle='В плюсе'
                    minusTitle='В минусе'
                    plus={data.data.plus}
                    minus={data.data.minus}
                  />

                  <Faq
                    items={data.data.faq}
                  />

                  <ArcanaFooter
                    title='Другие арканы'
                    items={getNextArcanaLinks(arcanaId, config.pagePath)}
                    allLink={{
                      href: config.pagePath,
                      text: 'Смотреть все арканы',
                    }}
                    relatedLinks={{
                      title: 'Другие разделы',
                      items: [
                        {
                          href: `${config.relatedPagePath}/${data.arcana.id}-${ARCANA_SLUG[data.arcana.id]}`,
                          text: `${data.arcana.id} аркан ${data.arcana.name} ${config.relatedTitleSuffix}`,
                        },
                        {
                          href: config.relatedPagePath,
                          text: config.relatedListText,
                        },
                      ],
                    }}
                  />

                  <CalcBanner
                    title='Твоя Матрица судьбы'
                    desc={config.calcBannerDesc}
                    btn='Открыть Матрицу'
                    link='~/tools/matrix'
                  />
                </Row>
              </Row>
            </Motion>
          )}

        <MenuFooter />
      </Layout.Content>
    </Layout>
  )
}
